import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCookieConsent } from '../../context/CookieConsentContext';
import { CONSENT_CATEGORIES, ConsentCategories } from '../../types/cookieConsent';

const CookiePreferencesModal: React.FC = () => {
  const {
    consentState,
    isPreferencesOpen,
    closePreferences,
    acceptAll,
    rejectNonEssential,
    savePreferences,
  } = useCookieConsent();

  // Local toggle state — initialized from stored consent or all-false
  const [localPrefs, setLocalPrefs] = useState<Omit<ConsentCategories, 'necessary'>>({
    analytics: consentState?.categories.analytics ?? false,
    functional: consentState?.categories.functional ?? false,
    marketing: consentState?.categories.marketing ?? false,
  });

  // Sync local state when the modal opens
  useEffect(() => {
    if (isPreferencesOpen) {
      setLocalPrefs({
        analytics: consentState?.categories.analytics ?? false,
        functional: consentState?.categories.functional ?? false,
        marketing: consentState?.categories.marketing ?? false,
      });
    }
  }, [isPreferencesOpen, consentState]);

  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Focus the close button when modal opens
  useEffect(() => {
    if (isPreferencesOpen && closeButtonRef.current) {
      closeButtonRef.current.focus();
    }
  }, [isPreferencesOpen]);

  // Trap focus inside modal
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        closePreferences();
        return;
      }
      if (e.key !== 'Tab') return;

      const focusable = modalRef.current?.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable || focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    [closePreferences]
  );

  const toggle = (key: keyof Omit<ConsentCategories, 'necessary'>) => {
    setLocalPrefs((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = () => savePreferences(localPrefs);

  const handleAcceptAll = () => {
    setLocalPrefs({ analytics: true, functional: true, marketing: true });
    acceptAll();
  };

  const handleRejectAll = () => {
    setLocalPrefs({ analytics: false, functional: false, marketing: false });
    rejectNonEssential();
  };

  if (!isPreferencesOpen) return null;

  return (
    <div
      className="cookie-modal-overlay"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) closePreferences();
      }}
      data-testid="cookie-preferences-overlay"
    >
      <div
        className="cookie-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-modal-title"
        ref={modalRef}
        onKeyDown={handleKeyDown}
        data-testid="cookie-preferences-modal"
      >
        {/* Header */}
        <div className="cookie-modal__header">
          <h2 id="cookie-modal-title" className="cookie-modal__title">
            Cookie Preferences
          </h2>
          <button
            ref={closeButtonRef}
            className="cookie-modal__close"
            onClick={closePreferences}
            aria-label="Close cookie preferences"
            data-testid="cookie-modal-close"
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>

        {/* Body */}
        <div className="cookie-modal__body">
          <p className="cookie-modal__intro">
            Choose which cookies you allow LookBel to use. You can change these
            settings at any time using the Cookie Settings link in our footer.{' '}
            <Link to="/privacy" className="cookie-modal__link" onClick={closePreferences}>
              Privacy Policy
            </Link>
          </p>

          <ul className="cookie-modal__categories">
            {CONSENT_CATEGORIES.map((cat) => {
              const isChecked =
                cat.id === 'necessary'
                  ? true
                  : localPrefs[cat.id as keyof typeof localPrefs];

              return (
                <li key={cat.id} className="cookie-category">
                  <div className="cookie-category__header">
                    <div className="cookie-category__info">
                      <span className="cookie-category__title">{cat.title}</span>
                      {cat.required && (
                        <span className="cookie-category__badge" aria-label="Always active">
                          Always active
                        </span>
                      )}
                    </div>

                    {cat.required ? (
                      // Locked toggle for necessary
                      <div
                        className="cookie-toggle cookie-toggle--locked"
                        role="switch"
                        aria-checked="true"
                        aria-label={`${cat.title} — always active`}
                        tabIndex={0}
                      >
                        <div className="cookie-toggle__thumb" />
                      </div>
                    ) : (
                      <button
                        role="switch"
                        aria-checked={isChecked as boolean}
                        aria-label={`${isChecked ? 'Disable' : 'Enable'} ${cat.title} cookies`}
                        className={`cookie-toggle${isChecked ? ' cookie-toggle--on' : ''}`}
                        onClick={() =>
                          toggle(cat.id as keyof Omit<ConsentCategories, 'necessary'>)
                        }
                        data-testid={`cookie-toggle-${cat.id}`}
                      >
                        <div className="cookie-toggle__thumb" />
                      </button>
                    )}
                  </div>
                  <p className="cookie-category__description">{cat.description}</p>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Footer */}
        <div className="cookie-modal__footer">
          <button
            className="cookie-modal__btn cookie-modal__btn--reject"
            onClick={handleRejectAll}
            data-testid="cookie-modal-reject"
          >
            Reject Non-Essential
          </button>
          <div className="cookie-modal__footer-right">
            <button
              className="cookie-modal__btn cookie-modal__btn--accept"
              onClick={handleAcceptAll}
              data-testid="cookie-modal-accept-all"
            >
              Accept All
            </button>
            <button
              className="cookie-modal__btn cookie-modal__btn--save"
              onClick={handleSave}
              data-testid="cookie-modal-save"
            >
              Save Preferences
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiePreferencesModal;
